import { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FilterView } from '@/shared/types/analytics';

interface EditFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string, viewId: string) => Promise<void>;
  activeView?: FilterView;
}

export function EditFilterDialog({
  open,
  onOpenChange,
  onSave,
  activeView,
}: EditFilterDialogProps) {
  const { t } = useTranslation();
  const [localName, setLocalName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Compute the display name directly
  const displayName = open && activeView ? activeView.name : localName;

  // Reset only when dialog closes
  useEffect(() => {
    if (!open) {
      setLocalName('');
    }
  }, [open]);

  const handleSave = async () => {
    if (!displayName.trim() || !activeView) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(displayName.trim(), activeView.id);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update filter view name:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && displayName.trim() && !isSaving) {
      handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('brain_logs.edit_filter_view_name')}</DialogTitle>
          <DialogDescription>{t('brain_logs.edit_filter_view_name_description')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="filter-name">{t('brain_logs.filter_view_name')}</Label>
            <Input
              id="filter-name"
              value={displayName}
              onChange={(e) => setLocalName(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={50}
              autoFocus
            />
            <div className="text-right text-xs text-muted-foreground">
              {displayName.length}/50 {t('common.characters')}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost-neutral" onClick={() => onOpenChange(false)} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!displayName.trim() || isSaving}>
            {isSaving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
